/**
 * Which of the caller's organizations this TAB is acting in.
 *
 * <p>Session storage, not local storage, and that is the feature rather than a detail:
 * a platform administrator with one customer open in each of two tabs must not have the
 * second tab silently re-point the first. Each tab keeps its own selection, and a new
 * tab starts from the account's default (no header at all).
 *
 * <p>Framework-free on purpose. The axios interceptor reads it outside React on every
 * request; React subscribes through `useSyncExternalStore` in `useOrganizationSelection`.
 *
 * <p>Every storage access is guarded: Safari private windows and blocked site data both
 * throw, and a selection that does not persist across a reload is far better than a
 * client that cannot send a single request.
 */
const STORAGE_KEY = 'fleettrace.organizationId';

type Listener = () => void;

const listeners = new Set<Listener>();

function read(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

let current: string | null = read();

export const organizationStore = {
  /** The selected organization id, or null to let the server pick the default. */
  get(): string | null {
    return current;
  },

  set(organizationId: string | null) {
    if (organizationId === current) return;
    current = organizationId;
    try {
      if (organizationId) sessionStorage.setItem(STORAGE_KEY, organizationId);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // In-memory only for this tab. Still correct until the next reload.
    }
    listeners.forEach((listener) => listener());
  },

  clear() {
    organizationStore.set(null);
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

/** The request header the backend reads the selection from. */
export const ORGANIZATION_HEADER = 'X-Organization-Id';
